import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnInit, SimpleChanges,
} from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { MenuAction } from '@anecdotes/design-system/dropdown-menu';
import { firstValueFrom, map, Observable } from 'rxjs';
import { ContactList, TrustCenterFacadeService, TrustCenterSharedModalsService } from '@anecdotes/shared-trust-center';
import { TrustCenterModalService } from '../../../../services';
import { AlertService } from '@anecdotes/design-system/alert';
import { EvidenceFacadeService } from '@anecdotes/data-store';

@Component({
  selector: 'app-contact-list',
  templateUrl: './contact-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactListComponent implements OnInit, OnChanges {
  @HostBinding('class')
  private readonly class = 'flex flex-col gap-2.5 p-3 border border-navy-50 rounded-md bg-navy-10';

  readonly menuPosition: ConnectedPosition = {
    originX: 'end',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'top',
  };

  readonly menuActions: MenuAction[] = [
    {
      translationKey: this.buildTranslationKey('menuActions.edit'),
      icon: 'edit',
      action: this.openAddContactListSidePanel.bind(this),
    },
    {
      translationKey: this.buildTranslationKey('menuActions.remove'),
      icon: 'remove',
      action: this.deleteContactList.bind(this),
    },
  ];

  hasSalesforceAutomaticApprovals: boolean;

  evidenceName$: Observable<string>;
  profileName$: Observable<string>;

  @Input({ required: true })
  contactList: ContactList;

  constructor(
    private readonly trustCenterFacade: TrustCenterFacadeService,
    private readonly trustCenterModalService: TrustCenterModalService,
    private readonly alertService: AlertService,
    private readonly evidenceFacade: EvidenceFacadeService,
    private readonly cd: ChangeDetectorRef,
    private readonly trustCenterSharedModalsService: TrustCenterSharedModalsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('contactList' in changes) {
      this.profileName$ = this.trustCenterFacade.getProfileById(this.contactList.profile_id)
        .pipe(map((profile) => profile.name));

      this.evidenceName$ = this.evidenceFacade.getEvidence(this.contactList.evidence_id)
        .pipe(map((evidence) => evidence.evidence_display_name || evidence.evidence_name));
    }
  }

  async ngOnInit(): Promise<void> {
    this.hasSalesforceAutomaticApprovals = await firstValueFrom(this.trustCenterFacade.hasSalesforceAutomaticApprovals(this.contactList.id));
    this.cd.detectChanges();
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.settings.automations.salesforceAutomaticApprovals.${key}`;
  }

  async updateSalesforceAutomaticApprovalsFlag(listId: string, hasAutomaticApprovals: boolean): Promise<void> {
    const previousValue = this.hasSalesforceAutomaticApprovals;
    this.hasSalesforceAutomaticApprovals = hasAutomaticApprovals;
    this.cd.detectChanges();

    try {
      await this.trustCenterFacade.updateContactList(listId, { enabled: hasAutomaticApprovals });
    } catch {
      this.hasSalesforceAutomaticApprovals = previousValue;
      this.cd.detectChanges();
    }
  }

  private openAddContactListSidePanel(): void {
    this.trustCenterModalService.openAddContactListSidePanel(this.contactList.evidence_id, this.contactList);
  }

  private async deleteContactList(): Promise<void> {
    if (await this.trustCenterSharedModalsService.openRemoveContactListConfirmationModal(this.buildTranslationKey('deleteConfirmationModal'))) {
      try {
        await this.trustCenterFacade.deleteContactList(this.contactList.id, true);
      } catch {
        this.alertService.openActionErrorAlert({
          customBaseTranslationKey: this.buildTranslationKey('deleteError'),
        });
      }
    }
  }
}
